import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    ReferenceInput,
    SelectInput,
    useRecordContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

/**
 * Inline notice pointing HR users to the Employees page for
 * employment-date management (hire date / termination date).
 * The `employees` resource holds employment accounting data
 * (join_date, termination_date) while this `users` resource holds
 * identity/Azure AD data.
 *
 * @returns {React.ReactElement | null} A styled notice with a link, or null
 *   when no record is loaded yet.
 */
const EmployeesLink: React.FC = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <p style={{ margin: '0 0 16px', color: '#555', fontSize: '0.875rem' }}>
            Hire date and termination date are managed on the{' '}
            <Link to={`/employees/${record.id}`}>
                Employees page
            </Link>
            .
        </p>
    );
};

/**
 * Edit page for a single user record (Azure AD identity data).
 *
 * Renders the react-admin `Edit` form with fields for:
 * - Employee ID, full name, and email (all read-only; sourced from Azure AD)
 * - Work Schedule assignment (editable via `ReferenceInput`)
 *
 * Note: `termination_date` and `hire_date` live in the `employees` table
 * (employment accounting data). The {@link EmployeesLink} component provides
 * a direct link so HR users can navigate there without having to search.
 */
export const UserEdit: React.FC = () => (
    <Edit>
        <SimpleForm>
            <EmployeesLink />
            <TextInput source="employee_id" label="Employee ID" disabled />
            <TextInput source="full_name" label="Full Name" disabled />
            <TextInput source="email" label="Email" disabled />
            <ReferenceInput source="work_schedule_id" reference="work-schedules">
                <SelectInput optionText="name" label="Work Schedule" />
            </ReferenceInput>
        </SimpleForm>
    </Edit>
);
